import React from 'react';
import useResponsive from '../../hooks/useResponsive';

const SUBJECT_TYPE_META = {
  IT: { label: 'IT', icon: 'fas fa-laptop-code', color: 'text-blue-600' },
  MEDI: { label: 'MEDI', icon: 'fas fa-paint-brush', color: 'text-purple-600' },
  CORE: { label: 'CORE', icon: 'fas fa-graduation-cap', color: 'text-green-600' },
  KV_FACH: { label: 'KV', icon: 'fas fa-building', color: 'text-slate-600' },
  KUNDENDIALOG_FACH: { label: 'KUNDENDIALOG', icon: 'fas fa-headset', color: 'text-emerald-600' },
  DETAILHANDEL_FACH: { label: 'DETAILHANDEL', icon: 'fas fa-store', color: 'text-orange-600' },
};

const SubjectSelectionModal = ({
  show,
  selectedSemester,
  availableSubjects,
  selectedSubjects,
  subjectFilters,
  subjectSearchTerm,
  loading,
  onClose,
  onSave,
  onSubjectToggle,
  onFilterToggle,
  onSearchChange,
  onClearFilters
}) => {
  const { isMobile, isTablet } = useResponsive();

  if (!show) return null;

  const normalizedType = (subject) => subject.subjectType || subject.subject_type || 'CORE';

  const availableFilterTypes = Array.from(
    new Set(availableSubjects.map((subject) => normalizedType(subject)).filter(Boolean))
  ).sort((a, b) => {
    if (a === 'CORE') return -1;
    if (b === 'CORE') return 1;
    return a.localeCompare(b);
  });

  const getFilteredSubjects = () => {
    let filtered = availableSubjects;
    
    // Filter by subject type
    if (subjectFilters.length > 0) {
      filtered = filtered.filter((s) => subjectFilters.includes(normalizedType(s)));
    }
    
    // Filter by search term
    if (subjectSearchTerm) {
      filtered = filtered.filter(s => 
        (s.name && s.name.toLowerCase().includes(subjectSearchTerm.toLowerCase())) ||
        (s.subjectCode && s.subjectCode.toLowerCase().includes(subjectSearchTerm.toLowerCase())) ||
        (s.subject_code && s.subject_code.toLowerCase().includes(subjectSearchTerm.toLowerCase())) ||
        (s.description && s.description.toLowerCase().includes(subjectSearchTerm.toLowerCase()))
      );
    }
    
    return filtered;
  };

  const filteredSubjects = getFilteredSubjects();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div
        className={`flex w-full flex-col overflow-hidden rounded-2xl border border-pale-dawn bg-white shadow-brand-lg ${
          isMobile
            ? "max-w-sm max-h-[95vh]"
            : isTablet
            ? "max-w-2xl max-h-[90vh]"
            : "max-w-4xl max-h-[90vh]"
        }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 border-b border-pale-dawn px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sunrise-50 text-sunrise-red">
                <i className="fas fa-book-open text-sm"></i>
              </div>

              <div className="min-w-0">
                <h2 className="text-xl font-bold text-grey-dusk sm:text-2xl">
                  Select Subjects
                </h2>
                <p className="mt-1 text-sm text-slate-cloud sm:text-base">
                  Choose subjects for {selectedSemester?.name}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-pale-dawn bg-white text-gray-400 transition-colors hover:bg-pale-dawn/40 hover:text-grey-dusk"
              aria-label="Close modal"
            >
              <i className="fas fa-times text-sm"></i>
            </button>
          </div>

          {/* Search + Filters */}
          <div className="mt-5 space-y-4">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <i className="fas fa-search text-sm text-slate-cloud"></i>
              </div>

              <input
                type="text"
                value={subjectSearchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={
                  isMobile
                    ? "Search subjects..."
                    : "Search by subject name, code or description..."
                }
                className="input-brand w-full pl-10 pr-10"
              />

              {subjectSearchTerm && (
                <button
                  type="button"
                  onClick={() => onSearchChange("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 transition-colors hover:text-grey-dusk"
                  aria-label="Clear search"
                >
                  <i className="fas fa-times text-sm"></i>
                </button>
              )}
            </div>

            <div className="rounded-2xl border border-pale-dawn bg-pale-dawn/30 p-3 sm:p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-grey-dusk sm:text-base">
                  Filter by Category
                </h3>

                {subjectFilters.length > 0 && (
                  <button
                    type="button"
                    onClick={onClearFilters}
                    className="text-xs font-medium text-sunrise-red transition-colors hover:text-sunrise-600"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {availableFilterTypes.map((filterType) => {
                  const meta = SUBJECT_TYPE_META[filterType] || {
                    label: filterType,
                    icon: "fas fa-book",
                    color: "text-gray-600",
                  };

                  const isActive = subjectFilters.includes(filterType);

                  return (
                    <button
                      key={filterType}
                      type="button"
                      onClick={() => onFilterToggle(filterType)}
                      className={`inline-flex items-center rounded-full border px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${
                        isActive
                          ? "border-sunrise-red bg-sunrise-red/10 text-sunrise-red"
                          : "border-gray-200 bg-white text-grey-dusk hover:border-slate-cloud hover:bg-gray-50"
                      }`}
                    >
                      <i
                        className={`mr-2 text-xs ${
                          isActive ? "text-sunrise-red" : meta.color
                        } ${meta.icon}`}
                      ></i>
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          {filteredSubjects.length > 0 ? (
            <div
              className={`grid gap-3 ${
                isMobile
                  ? "grid-cols-1"
                  : isTablet
                  ? "grid-cols-1 sm:grid-cols-2"
                  : "grid-cols-1 md:grid-cols-2"
              }`}
            >
              {filteredSubjects.map((subject) => {
                const subjectCode = subject.subjectCode || subject.subject_code;
                const subjectType = subject.subjectType || subject.subject_type;
                const isSelected = selectedSubjects.has(subject.id);

                const typeBadgeClass =
                  subjectType === "IT"
                    ? "bg-informatik-blue-50 text-informatik-blue-700 border-informatik-blue-200"
                    : subjectType === "MEDI"
                    ? "bg-mediamatik-purple-50 text-mediamatik-purple-700 border-mediamatik-purple-200"
                    : subjectType === "CORE"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-gray-50 text-gray-700 border-gray-200";

                return (
                  <button
                    key={subject.id}
                    type="button"
                    onClick={() => onSubjectToggle(subject.id)}
                    className={`w-full rounded-2xl border p-4 text-left transition-all ${
                      isSelected
                        ? "border-sunrise-red bg-sunrise-red/5 shadow-sm"
                        : "border-pale-dawn bg-white hover:border-slate-cloud hover:bg-pale-dawn/20"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="pt-0.5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onSubjectToggle(subject.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 rounded border-gray-300 text-sunrise-red focus:ring-sunrise-red"
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="text-sm font-semibold leading-5 text-grey-dusk sm:text-base">
                              {subjectCode ? `${subjectCode} - ${subject.name}` : subject.name}
                            </h3>

                            {subject.description && (
                              <p className="mt-1 line-clamp-2 text-xs text-slate-cloud sm:text-sm">
                                {subject.description}
                              </p>
                            )}
                          </div>

                          <div
                            className={`mt-0.5 inline-flex shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${typeBadgeClass}`}
                          >
                            {subjectType}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : filteredSubjects.length === 0 && availableSubjects.length > 0 ? (
            <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-pale-dawn bg-pale-dawn/20 px-6 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white text-slate-cloud shadow-sm">
                <i className="fas fa-search text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-grey-dusk">
                No matching subjects
              </h3>
              <p className="mt-2 max-w-sm text-sm text-slate-cloud">
                No subjects match your current search or filter selection.
              </p>
              <button
                type="button"
                onClick={onClearFilters}
                className="mt-4 inline-flex items-center rounded-lg border border-pale-dawn bg-white px-4 py-2 text-sm font-medium text-sunrise-red transition-colors hover:bg-pale-dawn/40"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-pale-dawn bg-pale-dawn/20 px-6 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white text-slate-cloud shadow-sm">
                <i className="fas fa-book text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-grey-dusk">
                No subjects available
              </h3>
              <p className="mt-2 text-sm text-slate-cloud">
                There are currently no subjects to choose from.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-pale-dawn px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-cloud">
              <div className="font-medium text-grey-dusk">
                {selectedSubjects.size} subject
                {selectedSubjects.size !== 1 ? "s" : ""} selected
              </div>

              {(subjectSearchTerm || subjectFilters.length > 0) && (
                <div className="mt-1 text-xs">
                  Showing {filteredSubjects.length} of {availableSubjects.length}
                </div>
              )}
            </div>

            <div className="flex w-full flex-col-reverse gap-3 sm:w-auto sm:flex-row sm:items-center">
              <button
                onClick={onClose}
                disabled={loading}
                className="inline-flex w-full items-center justify-center rounded-xl border border-pale-dawn bg-white px-5 py-2.5 text-sm font-semibold text-grey-dusk transition-colors hover:bg-pale-dawn/40 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Cancel
              </button>

              <button
                onClick={onSave}
                disabled={loading || selectedSubjects.size === 0}
                className="inline-flex w-full items-center justify-center rounded-xl bg-sunrise-red px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sunrise-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {loading ? "Saving..." : "Save Selections"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectSelectionModal;
